import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { getOrder } from "@/lib/api";

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getOrder(id).then(setOrder).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="pt-[140px] pb-40 text-center">
        <p className="font-display text-3xl text-ink">Order not found</p>
        <Link to="/shop" className="text-forest font-semibold mt-4 inline-block">Back to shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="order-confirmation-page" className="bg-bone pt-[72px] min-h-screen">
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
          className="w-20 h-20 rounded-full bg-forest flex items-center justify-center mx-auto"
        >
          <Check className="w-10 h-10 text-white" strokeWidth={2} />
        </motion.div>

        <h1 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-8 leading-[1.05]">
          Your protocol is on its way.
        </h1>
        <p className="text-stone mt-4 leading-relaxed">
          Thank you{order ? `, ${order.full_name.split(" ")[0]}` : ""}. Our medical team will review your order,
          and you'll receive a confirmation at {order?.email || "your email"} shortly.
        </p>

        {order && (
          <div className="mt-10 text-left rounded-2xl border border-line bg-sand p-6">
            <div className="flex justify-between items-center mb-5">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone">Order</p>
              <p className="text-xs font-mono text-stone" data-testid="order-id">#{order.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="space-y-3">
              {order.items.map((i) => (
                <div key={i.slug} className="flex justify-between text-sm">
                  <span className="text-ink">{i.quantity} × {i.name}</span>
                  <span className="text-ink font-semibold">${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-line mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-stone"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : `$${order.shipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-base"><span className="font-semibold text-ink">Total</span><span className="font-display text-xl text-ink">${order.total.toFixed(2)}</span></div>
            </div>
            <p className="text-xs text-stone mt-4">
              Shipping to {order.address}, {order.city}, {order.state} {order.zip_code}
            </p>
          </div>
        )}

        <Link
          to="/shop"
          data-testid="continue-shopping-btn"
          className="mt-10 inline-block rounded-full bg-forest text-[#14100F] px-8 py-3.5 text-base font-semibold hover:bg-forest-dark transition-colors"
        >
          Continue exploring
        </Link>
      </div>
    </div>
  );
}
